import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ApiErrorResponse,
  getKuchikomiApiClient,
  handleError,
} from '../../services/network'
import { Kuchikomi } from '../../clients/kuchikomi_api_client'
import { useSnackBar } from '../../providers/snack_bar_provider'
import { useAuthAuthorized } from '../auth/use_auth'

type Handler = {
  load: (cursor: number) => Promise<void>
}

/**
 * 自社のクチコミを取得するフック
 */
export const useOwnKuchikomis = (
  getLimit = 20
): {
  initialized: boolean
  loading: boolean
  totalCount: number
  canLoadMore: boolean
  kuchikomis: Kuchikomi[]
  handler: Handler
} => {
  const apiClient = getKuchikomiApiClient()
  const [kuchikomis, setKuchikomis] = useState<Kuchikomi[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [initialized, setInitialized] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)
  const { showSnackBar } = useSnackBar()
  const { user } = useAuthAuthorized()

  const cursor = kuchikomis.length

  const canLoadMore = useMemo(() => {
    return cursor < totalCount
  }, [cursor, totalCount])

  const load = useCallback(
    async (cursor: number) => {
      setLoading(true)
      try {
        const response = await apiClient.enterprises.kuchikomisList(
          user.kuchikomiEnterpriseId,
          {
            cursor: cursor,
            limit: getLimit,
          }
        )
        setKuchikomis(response.data.data)
        setTotalCount(response.data.total)
      } catch (e) {
        handleError(e as ApiErrorResponse, showSnackBar)
      } finally {
        setLoading(false)
        setInitialized(true)
      }
    },
    [getLimit, totalCount]
  )

  useEffect(() => {
    load(0)
  }, [])

  return {
    initialized,
    loading,
    totalCount,
    canLoadMore,
    kuchikomis,
    handler: { load },
  }
}
